/**
 * Centralized in-memory cache for GET /GetProductCategory.
 * Shared by KitchenProducts (list/filter) and AddKitchenProduct (form dropdowns).
 */

import {
  getProductCategoryAPI,
  parseProductCategoryAPIResponse,
} from '../context-api/api-services/Products.service';

const cache = {
  /** Normalized shape from `normalizeProductCategoryData` / API parse. */
  data: null,
  refreshedAt: null,
};

/**
 * Drop cached categories so the next fetch hits the network.
 */
export function clearProductCategoryCache() {
  cache.data = null;
  cache.refreshedAt = null;
}

/**
 * @returns {{ data: object, refreshedAt: number } | null}
 */
export function getProductCategoryCacheSnapshot() {
  if (cache.data == null) return null;
  return { data: cache.data, refreshedAt: cache.refreshedAt };
}

/**
 * Returns normalized category groups from cache when available, otherwise fetches once.
 *
 * @param {{ forceRefresh?: boolean }} [options] Pass `forceRefresh: true` after admin changes or when the user taps reload (Add Product).
 * @returns {Promise<{ ok: true, data: object, message?: string } | { ok: false, data: null, message: string }>}
 */
export async function fetchProductCategoryData({ forceRefresh = false } = {}) {
  if (!forceRefresh && cache.data != null) {
    return { ok: true, data: cache.data, message: '' };
  }

  if (forceRefresh) {
    clearProductCategoryCache();
  }

  const res = await getProductCategoryAPI();
  const parsed = parseProductCategoryAPIResponse(res);
  if (parsed.ok && parsed.data) {
    cache.data = parsed.data;
    cache.refreshedAt = Date.now();
  }
  return parsed;
}
